import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import CustomerStats from './components/CustomerStats';
import CustomerFilters from './components/CustomerFilters';
import CustomerCard from './components/CustomerCard';
import CustomerForm from './components/CustomerForm';
import CustomerDetailsModal from './components/CustomerDetailsModal';

const CustomerManagement = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);

  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    preferredContact: 'email',
    status: 'active',
    notes: '',
    propertyType: 'residential',
    propertySize: '',
    source: 'website'
  });

  // Real-time listeners
  useEffect(() => {
    const unsubscribers = [];

    // Customers listener
    const customersQuery = query(
      collection(db, 'customers'),
      orderBy('createdAt', 'desc')
    );
    const customersUnsub = onSnapshot(customersQuery, (snapshot) => {
      const customersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    });
    unsubscribers.push(customersUnsub);

    // Service History listener
    const serviceHistoryUnsub = onSnapshot(
      collection(db, 'service_history'),
      (snapshot) => {
        const historyData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setServiceHistory(historyData);
      }
    );
    unsubscribers.push(serviceHistoryUnsub);

    setIsLoading(false);

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  // Customer Management Functions
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const customerData = {
        ...customerForm,
        fullName: `${customerForm.firstName} ${customerForm.lastName}`,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      };

      if (editingCustomer) {
        await updateDoc(doc(db, 'customers', editingCustomer.id), customerData);
      } else {
        await addDoc(collection(db, 'customers'), {
          ...customerData,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          totalServices: 0,
          totalSpent: 0,
          lastService: null
        });
      }

      resetCustomerForm();
    } catch (error) {
      console.error('Error managing customer:', error);
    }
  };

  // Normalize row keys: "First Name" -> firstName, "Street Address" -> streetAddress, etc.
  const normalizeRow = (row) => {
    const toCamel = (s) => String(s || '').trim().toLowerCase().replace(/\s+(.)/g, (_, c) => c.toUpperCase());
    const out = {};
    Object.keys(row).forEach((key) => {
      const camel = toCamel(key).replace(/\s/g, '');
      const val = row[key];
      out[camel] = typeof val === 'string' ? val : val != null ? String(val) : '';
    });
    return out;
  };

  const buildCustomerFromRow = (row) => {
    const trim = (v) => (v && String(v).trim()) || '';
    const email = trim(row.email);
    const phone = trim(row.phone);
    const firstName = trim(row.firstName);
    const lastName = trim(row.lastName);

    // Standard format: First Name, Last Name, Email, Phone
    if (firstName && lastName && (email || phone)) {
      return {
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`,
        email,
        phone,
        address: trim(row.address) || trim(row.streetAddress),
        city: trim(row.city),
        state: trim(row.state),
        zipCode: trim(row.zipCode ?? row.zipcode ?? row.zip),
        preferredContact: trim(row.preferredContact) || 'email',
        status: trim(row.status) || 'active',
        notes: trim(row.notes) || '',
        propertyType: trim(row.propertyType) || 'residential',
        propertySize: trim(row.propertySize) || '',
        source: 'upload',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
        totalServices: 0,
        totalSpent: 0,
        lastService: null,
        openBalance: parseFloat(row.openBalance ?? row.openbalance) || 0
      };
    }

    // Property-style format: Name, Company name, Street Address, City, State, Zip, Phone, Email, Open balance
    const name = trim(row.name);
    const companyName = trim(row.companyname ?? row.companyName);
    const streetAddress = trim(row.streetAddress ?? row.streetaddress ?? row.address);
    const displayName = name || companyName || (streetAddress && streetAddress.split('\n')[0]) || 'Customer';
    if (!displayName || (!email && !phone)) return null;

    const parts = displayName.trim().split(/\s+/);
    const first = parts[0] || '';
    const rest = parts.slice(1).join(' ') || '';

    return {
      firstName: first || '—',
      lastName: rest || displayName,
      fullName: displayName,
      email: email || '',
      phone: phone || '',
      address: streetAddress,
      city: trim(row.city),
      state: trim(row.state),
      zipCode: trim(row.zipCode ?? row.zipcode ?? row.zip),
      preferredContact: email ? 'email' : 'phone',
      status: 'active',
      notes: companyName && companyName !== displayName ? `Company: ${companyName}` : trim(row.notes),
      propertyType: 'residential',
      propertySize: '',
      source: 'upload',
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      totalServices: 0,
      totalSpent: 0,
      lastService: null,
      openBalance: parseFloat(row.openBalance ?? row.openbalance) || 0
    };
  };

  const processRowsIntoFirestore = async (data) => {
    let successCount = 0;
    let errorCount = 0;
    for (const raw of data) {
      const row = normalizeRow(raw);
      const customerData = buildCustomerFromRow(row);
      if (!customerData) {
        errorCount++;
        continue;
      }
      try {
        await addDoc(collection(db, 'customers'), customerData);
        successCount++;
      } catch (error) {
        console.error('Error adding customer:', error);
        errorCount++;
      }
    }
    setUploading(false);
    alert(`Upload complete! ${successCount} customers added. ${errorCount} rows skipped (need at least Email or Phone and a name/address).`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);

    setUploading(true);

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const ab = e.target.result;
          const workbook = XLSX.read(ab, { type: 'array' });
          const firstSheet = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheet];
          const data = XLSX.utils.sheet_to_json(worksheet);
          processRowsIntoFirestore(data);
        } catch (err) {
          console.error('Error parsing Excel:', err);
          setUploading(false);
          alert('Error reading Excel file. Use columns: Name, Street Address, City, State, Zip, Phone, Email (at least Email or Phone per row), or standard: First Name, Last Name, Email, Phone.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true,
        complete: async (results) => {
          await processRowsIntoFirestore(results.data || []);
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          setUploading(false);
          alert('Error parsing CSV file. Please check the format.');
        }
      });
    }
    event.target.value = '';
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer? This will also delete their service history.')) {
      try {
        await deleteDoc(doc(db, 'customers', customerId));
        
        // Delete associated service history
        const customerServices = serviceHistory.filter(s => s.customerId === customerId);
        for (const service of customerServices) {
          await deleteDoc(doc(db, 'service_history', service.id));
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const resetCustomerForm = () => {
    setEditingCustomer(null);
    setShowForm(false);
    setCustomerForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      preferredContact: 'email',
      status: 'active',
      notes: '',
      propertyType: 'residential',
      propertySize: '',
      source: 'website'
    });
  };

  const editCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zipCode: customer.zipCode || '',
      preferredContact: customer.preferredContact || 'email',
      status: customer.status || 'active',
      notes: customer.notes || '',
      propertyType: customer.propertyType || 'residential',
      propertySize: customer.propertySize || '',
      source: customer.source || 'website'
    });
    setShowForm(true);
  };

  const getCustomerServices = (customerId) => {
    return serviceHistory.filter(service => service.customerId === customerId);
  };

  const openCustomerModal = (customer) => {
    setModalCustomer(customer);
    setShowCustomerModal(true);
  };

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setModalCustomer(null);
  };

  const handleModalCustomerUpdate = async (updatedCustomer) => {
    try {
      await updateDoc(doc(db, 'customers', updatedCustomer.id), {
        ...updatedCustomer,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
      closeCustomerModal();
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      prospect: 'bg-blue-100 text-blue-800',
      archived: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.active;
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.phone?.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || customer.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Customer Management
          </h1>
          <p className="text-secondary-600">
            Manage customer profiles, service history, and relationships
          </p>
        </div>

        {/* Stats Cards */}
        <CustomerStats customers={customers} serviceHistory={serviceHistory} />

        {/* Search and Filters */}
        <CustomerFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          onAddCustomer={() => setShowForm(!showForm)}
          uploading={uploading}
          onFileUpload={handleFileUpload}
        />

        {/* Customer Form */}
        <CustomerForm
          showForm={showForm}
          editingCustomer={editingCustomer}
          customerForm={customerForm}
          onFormChange={setCustomerForm}
          onSubmit={handleCustomerSubmit}
          onCancel={resetCustomerForm}
        />

        {/* Customer List */}
        <div className="grid grid-cols-1 gap-3">
          {filteredCustomers.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onViewDetails={openCustomerModal}
              onEdit={editCustomer}
              onDelete={handleDeleteCustomer}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>

        {filteredCustomers.length === 0 && (
          <div className="card">
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-secondary-400" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">No Customers Found</h3>
              <p className="text-secondary-600">
                {searchTerm || filterStatus !== 'all' 
                  ? 'No customers match your search criteria.' 
                  : 'Get started by adding your first customer.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Customer Details Modal */}
      {showCustomerModal && modalCustomer && (
        <CustomerDetailsModal
          customer={modalCustomer}
          services={getCustomerServices(modalCustomer.id)}
          onClose={closeCustomerModal}
          onUpdate={handleModalCustomerUpdate}
          onDelete={() => {
            handleDeleteCustomer(modalCustomer.id);
            closeCustomerModal();
          }}
        />
      )}
    </div>
  );
};

export default CustomerManagement;